import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Grid, IconButton, Tooltip, Typography, TypographyProps } from '@mui/material';
import { isString } from 'es-toolkit/compat';
import React from 'react';
import { useClipboard } from 'use-clipboard-copy';

interface CopyableTextProps extends TypographyProps {
  children?: React.ReactNode;
  textForCopy?: string; // Optional prop to specify different text for copying
  tooltipText?: string; // Optional prop for tooltip text
  copyWithButton?: 'left' | 'right'; // Optional prop to enable copy with button
  copyButtonVisibility?: 'hover' | 'always';
  copyButtonProps?: React.ComponentProps<typeof IconButton>; // Optional props for the copy button
  copyIconProps?: React.ComponentProps<typeof ContentCopyIcon>; // Optional props for the copy icon
  parentGridProps?: React.ComponentProps<typeof Grid>; // Optional props for the parent Grid
}

const CopyableText = ({
  children,
  textForCopy = isString(children) ? children : '',
  tooltipText = 'Click to copy',
  copyWithButton, // Optional prop to enable copy with button
  copyButtonVisibility = 'hover',
  copyButtonProps = {},
  copyIconProps = {},
  parentGridProps = {},
  ...typographyProps
}: CopyableTextProps) => {
  const clipboard = useClipboard({ copiedTimeout: 1000 });

  const handleCopy = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    clipboard.copy(textForCopy);
  };
  const textComponent = children ? (
    <Tooltip title={!copyWithButton && (clipboard.copied ? 'Copied!' : tooltipText)} placement="top">
      <Typography
        variant="body1"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        sx={{ cursor: 'pointer' }}
        onClick={!copyWithButton ? handleCopy : undefined}
        {...typographyProps}
      >
        {children}
      </Typography>
    </Tooltip>
  ) : null;
  const copyButton = copyWithButton ? (
    <Grid item xs={copyWithButton === 'left' && textComponent ? true : undefined}>
      <Tooltip title={clipboard.copied ? 'Copied!' : tooltipText} placement="top">
        <IconButton {...copyButtonProps} onClick={handleCopy} color="inherit">
          <ContentCopyIcon {...copyIconProps} />
        </IconButton>
      </Tooltip>
    </Grid>
  ) : null;

  if (!copyButton) {
    return textComponent;
  }

  if (!textComponent) {
    return copyButton;
  }

  return (
    <Grid
      container
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      wrap="nowrap"
      {...parentGridProps}
      sx={{
        ...parentGridProps?.sx,
        '& button>svg': {
          opacity: copyButtonVisibility === 'always' ? 1 : 0,
          transition: 'opacity 0.3s',
        },
        ...(copyButtonVisibility === 'hover' ? { '&:hover button>svg': { opacity: 1 } } : undefined),
      }}
    >
      {copyWithButton === 'left' && copyButton}
      <Grid item xs={copyWithButton === 'right' ? true : undefined}>
        {textComponent}
      </Grid>
      {copyWithButton === 'right' && copyButton}
    </Grid>
  );
};

export default CopyableText;
